/**
 * Covermatic Studio image viewer and edit controller.
 */
(() => {
  const runtime = window.CovermaticRuntime || window.AlphaRuntime || {};
  window.CovermaticRuntime = runtime;
  window.AlphaRuntime = runtime;
  const { Config, State, Utils, Events } = runtime;
  const getService = (name) => runtime.getService?.(name);
  const logViewer = (...args) => console.info('[Covermatic viewer]', ...args);
  const warnViewer = (...args) => console.warn('[Covermatic viewer]', ...args);
  const errorViewer = (...args) => console.error('[Covermatic viewer]', ...args);
  const TaskManager = new Proxy({}, {
    get(_target, prop) { return runtime.TaskManager?.[prop]; }
  });

  class ImageViewerController {
    constructor(owner) {
      this.owner = owner;
      this.lineagePrevBtn = Utils.$('lineagePrevButton');
      this.lineageNextBtn = Utils.$('lineageNextButton');
      this.lineageStatus = Utils.$('lineageStatus');
      this.modalImageNav = Utils.$('imageModalNav');
      this.modalLoadMoreBtn = Utils.$('modalImageLoadMore');
      this.modalPrevBtn = Utils.$('modalImagePrev');
      this.modalNextBtn = Utils.$('modalImageNext');
      this.modalNextLoading = Utils.$('modalNextLoading');
      this.globalSequence = [];
      this.globalIndex = -1;
      this.globalAnchorUrl = null;
      this.lineageSequence = [];
      this.lineageIndex = -1;
      this.lineageRequestToken = 0;

      this.init();
    }

    init() {
      if (this.modalLoadMoreBtn) this.modalLoadMoreBtn.addEventListener('click', () => this.loadMoreModalImages());
      if (this.modalPrevBtn) this.modalPrevBtn.addEventListener('click', () => this.navigateGlobal(-1));
      if (this.modalNextBtn) this.modalNextBtn.addEventListener('click', () => this.navigateGlobal(1));
      if (this.lineagePrevBtn) this.lineagePrevBtn.addEventListener('click', () => this.navigateLineage(-1));
      if (this.lineageNextBtn) this.lineageNextBtn.addEventListener('click', () => this.navigateLineage(1));
      const makeOfficialBtn = Utils.$('#btn-free-make-official');
      if (makeOfficialBtn) {
        makeOfficialBtn.addEventListener('click', (event) => {
          event.preventDefault();
          this.openMakeOfficialForm();
        });
      }
      const createOfficialBtn = Utils.$('#btn-free-create-official');
      if (createOfficialBtn) {
        createOfficialBtn.addEventListener('click', (event) => {
          event.preventDefault();
          this.submitMakeOfficial();
        });
      }
    }

    handleDocumentClick(event) {
      if (event.target.closest('#btn-submit-edit')) {
        this.submitEdit();
        return true;
      }
      return false;
    }

    resetMakeOfficialForm() {
      Utils.$('#free-official-form')?.classList.add('hidden');
      const details = Utils.$('#free-official-details');
      if (details) details.value = '';
      const submitBtn = Utils.$('#btn-free-create-official');
      if (submitBtn) submitBtn.disabled = false;
    }

    openMakeOfficialForm() {
      Utils.toast('Free preview upgrades are no longer available. Use the paid editor.', 'warn');
    }

    submitMakeOfficial() {
      Utils.toast('Free preview upgrades are no longer available. Use the paid editor.', 'warn');
    }

    getModalTitle(record, url = State.currentUrl) {
      const guestInfo = url ? Config.guestImageMap[url] : null;
      let artistName = String(record?.artistName || guestInfo?.['Artist Name'] || '').trim();
      let songName = String(record?.songName || guestInfo?.['Song/Album Name'] || '').trim();

      if (!artistName && !songName && record) {
        const gallery = getService('gallery');
        const groupSequence = typeof gallery?.getGroupSequence === 'function'
          ? gallery.getGroupSequence(record)
          : [];
        const fallbackRecord = [...groupSequence, ...this.lineageSequence].find((item) => {
          const candidateArtist = String(item?.artistName || '').trim();
          const candidateSong = String(item?.songName || '').trim();
          return candidateArtist || candidateSong;
        });

        if (fallbackRecord) {
          artistName = String(fallbackRecord.artistName || '').trim();
          songName = String(fallbackRecord.songName || '').trim();
        }
      }

      if (artistName && songName) return `${artistName} - ${songName}`;
      return artistName || songName || '';
    }

    getNavigableUrls() {
      const seen = new Set();
      const urls = [];

      ['#grid button[data-url]', '#guest-examples-container button[data-url]'].forEach((selector) => {
        Utils.$$(selector).forEach((card) => {
          const url = card.dataset.url;
          if (!url || seen.has(url)) return;
          seen.add(url);
          urls.push(url);
        });
      });

      return urls;
    }

    setDirectionalButtonState(button, show, disabled) {
      if (!button) return;
      button.classList.toggle('hidden', !show);
      button.classList.toggle('inline-flex', show);
      button.disabled = !!disabled;
    }

    setGlobalNextButtonState(mode = 'next') {
      if (!this.modalNextBtn) return;
      const isLoadMore = mode === 'load-more';
      this.modalNextBtn.textContent = isLoadMore ? 'Load more' : 'Next';
      this.modalNextBtn.dataset.mode = isLoadMore ? 'load-more' : 'next';
      this.modalNextBtn.setAttribute('aria-label', isLoadMore ? 'Load more cover art' : 'Next cover art');
    }

    updateLineageStatus(label = 'Version history', show = false) {
      if (!this.lineageStatus) return;
      const labelNode = this.lineageStatus.querySelector('[data-lineage-label]');
      if (labelNode) {
        labelNode.textContent = label;
      } else {
        this.lineageStatus.textContent = label;
      }
      this.lineageStatus.classList.toggle('hidden', !show);
      this.lineageStatus.classList.toggle('flex', show);
    }

    hideLineageNavigation() {
      this.setDirectionalButtonState(this.lineagePrevBtn, false, true);
      this.setDirectionalButtonState(this.lineageNextBtn, false, true);
      this.updateLineageStatus('Version history', false);
      this.lineageSequence = [];
      this.lineageIndex = -1;
    }

    syncGlobalNavigation(activeInput) {
      const activeRecord = typeof activeInput === 'string' ? null : activeInput;
      const activeUrl = typeof activeInput === 'string' ? activeInput : activeInput?.url || State.currentUrl;
      this.globalSequence = this.getNavigableUrls();

      if (activeUrl && this.globalSequence.includes(activeUrl)) {
        this.globalAnchorUrl = activeUrl;
      } else if (activeRecord) {
        const gallery = getService('gallery');
        const groupSequence = typeof gallery?.getGroupSequence === 'function'
          ? gallery.getGroupSequence(activeRecord)
          : [];
        const anchorRecord = groupSequence.find((record) => record?.url && this.globalSequence.includes(record.url));
        if (anchorRecord?.url) this.globalAnchorUrl = anchorRecord.url;
      }

      const navigationUrl = activeUrl && this.globalSequence.includes(activeUrl)
        ? activeUrl
        : this.globalAnchorUrl;
      this.globalIndex = navigationUrl ? this.globalSequence.indexOf(navigationUrl) : -1;

      const showNav = this.globalIndex !== -1 && this.globalSequence.length > 1;
      const atEnd = this.globalIndex !== -1 && this.globalIndex >= (this.globalSequence.length - 1);
      const canLoadMore = Utils.isLoggedIn() && !!State.hasMore && atEnd;

      if (this.modalImageNav) {
        this.modalImageNav.classList.toggle('hidden', !(showNav || canLoadMore));
        this.modalImageNav.classList.toggle('flex', showNav || canLoadMore);
      }

      if (this.modalLoadMoreBtn) {
        this.modalLoadMoreBtn.classList.add('hidden');
        this.modalLoadMoreBtn.disabled = false;
      }

      if (this.modalPrevBtn) this.modalPrevBtn.disabled = !showNav || this.globalIndex <= 0;
      if (this.modalNextBtn) {
        this.modalNextBtn.disabled = !(showNav || canLoadMore);
        this.setGlobalNextButtonState(canLoadMore ? 'load-more' : 'next');
      }
    }

    syncLineageNavigation(activeRecord) {
      const gallery = getService('gallery');
      const resolvedRecord = gallery?.resolveRecord?.(activeRecord);

      if (!resolvedRecord?.fileName) {
        logViewer('Local lineage nav hidden: no file name on active record', {
          fileName: resolvedRecord?.fileName || null,
          recordId: resolvedRecord?.id ?? null,
        });
        this.hideLineageNavigation();
        return;
      }

      if (!this.lineageSequence.length) {
        logViewer('Local lineage nav waiting for server lineage payload', {
          fileName: resolvedRecord.fileName,
          recordId: resolvedRecord.id ?? null,
        });
        this.setDirectionalButtonState(this.lineagePrevBtn, false, true);
        this.setDirectionalButtonState(this.lineageNextBtn, false, true);
        this.updateLineageStatus('Loading versions', true);
        return;
      }

      this.lineageIndex = this.lineageSequence.findIndex((record) => {
        if (resolvedRecord.id != null && record.id != null) return record.id === resolvedRecord.id;
        if (resolvedRecord.fileName && record.fileName) return record.fileName === resolvedRecord.fileName;
        return record.url === resolvedRecord.url;
      });

      if (this.lineageIndex < 0) {
        warnViewer('Local lineage nav hidden: active record not present in server sequence', {
          fileName: resolvedRecord.fileName,
          recordId: resolvedRecord.id ?? null,
          sequenceSize: this.lineageSequence.length,
        });
        this.hideLineageNavigation();
        return;
      }

      const prevEnabled = this.lineageIndex > 0;
      const nextEnabled = this.lineageIndex >= 0 && this.lineageIndex < (this.lineageSequence.length - 1);
      const showControls = this.lineageSequence.length > 1;
      logViewer('Local lineage nav synced', {
        coverGroupId: resolvedRecord.coverGroupId ?? null,
        fileName: resolvedRecord.fileName,
        lineageDepth: resolvedRecord.lineageDepth,
        sequenceSize: this.lineageSequence.length,
        lineageIndex: this.lineageIndex,
        prevEnabled,
        nextEnabled,
      });

      this.setDirectionalButtonState(this.lineagePrevBtn, showControls, !prevEnabled);
      this.setDirectionalButtonState(this.lineageNextBtn, showControls, !nextEnabled);

      const label = this.lineageSequence.length > 0 && this.lineageIndex >= 0
        ? `Version ${this.lineageIndex + 1} of ${this.lineageSequence.length}`
        : 'Version history';
      this.updateLineageStatus(label, showControls);
    }

    syncDirectionalButtons(showNav, prevEnabled = showNav && this.globalIndex > 0, nextEnabled = showNav && this.globalIndex < (this.globalSequence.length - 1)) {
      const prevDisabled = !prevEnabled;
      const nextDisabled = !nextEnabled;

      if (this.modalImageNav) {
        this.modalImageNav.classList.toggle('hidden', !showNav);
        this.modalImageNav.classList.toggle('flex', showNav);
      }

      if (this.modalLoadMoreBtn) {
        this.modalLoadMoreBtn.classList.add('hidden');
        this.modalLoadMoreBtn.disabled = false;
      }

      if (this.modalPrevBtn) this.modalPrevBtn.disabled = prevDisabled;
      if (this.modalNextBtn) this.modalNextBtn.disabled = nextDisabled;
      this.setGlobalNextButtonState('next');
      this.hideLineageNavigation();
    }

    syncImageNavigation(activeInput) {
      const gallery = getService('gallery');
      const activeRecord = gallery?.resolveRecord?.(activeInput);
      this.syncGlobalNavigation(activeRecord || activeInput);
      this.syncLineageNavigation(activeRecord || activeInput);
    }

    async loadServerLineage(activeInput) {
      const gallery = getService('gallery');
      const resolvedRecord = gallery?.resolveRecord?.(activeInput);
      if (!resolvedRecord?.fileName || !gallery || typeof gallery.fetchLineage !== 'function') {
        warnViewer('Cannot request server lineage without a file name', {
          fileName: resolvedRecord?.fileName || null,
          recordId: resolvedRecord?.id ?? null,
        });
        this.hideLineageNavigation();
        return;
      }

      const requestToken = ++this.lineageRequestToken;
      this.lineageSequence = [];
      this.lineageIndex = -1;
      this.updateLineageStatus('Loading versions', true);
      logViewer('Requesting server lineage for modal record', {
        fileName: resolvedRecord.fileName,
        recordId: resolvedRecord.id ?? null,
      });

      try {
        const sequence = await gallery.fetchLineage(resolvedRecord.fileName);
        if (requestToken !== this.lineageRequestToken) return;
        this.lineageSequence = Array.isArray(sequence) ? sequence : [];
        logViewer('Server lineage sequence ready for modal record', {
          fileName: resolvedRecord.fileName,
          sequenceSize: this.lineageSequence.length,
        });
      } catch (error) {
        if (requestToken !== this.lineageRequestToken) return;
        errorViewer('Failed to load server lineage', error);
        this.lineageSequence = [];
      } finally {
        if (requestToken !== this.lineageRequestToken) return;
        const activeRecord = State.currentImageRecord || resolvedRecord;
        this.syncLineageNavigation(activeRecord);
        const modalTitle = Utils.$('#modal-title');
        if (modalTitle) modalTitle.textContent = this.getModalTitle(activeRecord, activeRecord?.url || State.currentUrl);
      }
    }

    setModalNextLoading(isLoading) {
      if (this.modalNextLoading) this.modalNextLoading.classList.toggle('hidden', !isLoading);
      if (this.modalLoadMoreBtn) this.modalLoadMoreBtn.disabled = !!isLoading;
      if (this.modalPrevBtn) this.modalPrevBtn.disabled = !!isLoading;
      if (this.modalNextBtn) this.modalNextBtn.disabled = !!isLoading;
      if (this.lineagePrevBtn) this.lineagePrevBtn.disabled = !!isLoading || this.lineagePrevBtn.disabled;
      if (this.lineageNextBtn) this.lineageNextBtn.disabled = !!isLoading || this.lineageNextBtn.disabled;
    }

    async loadMoreModalImages() {
      const gallery = getService('gallery');
      if (!Utils.isLoggedIn() || !State.hasMore || !gallery || typeof gallery.fetchNextPage !== 'function') return;

      this.setModalNextLoading(true);
      try {
        await gallery.fetchNextPage();
      } catch (error) {
        errorViewer('Failed to load more modal images', error);
      } finally {
        this.setModalNextLoading(false);
        this.syncImageNavigation(State.currentImageRecord || State.currentUrl);
      }
    }

    async loadMoreAndAdvance(currentIndex) {
      const gallery = getService('gallery');
      if (!Utils.isLoggedIn() || !State.hasMore || !gallery || typeof gallery.fetchNextPage !== 'function') return false;

      this.setModalNextLoading(true);
      try {
        const previousLength = this.globalSequence.length;
        await gallery.fetchNextPage();
        this.globalSequence = this.getNavigableUrls();

        const nextRecordUrl = this.globalSequence[currentIndex + 1];
        if (nextRecordUrl) {
          this.openImage(nextRecordUrl);
          return true;
        }

        if (this.globalSequence.length <= previousLength) {
          Utils.toast('No more covers', 'info');
        } else {
          this.syncImageNavigation(State.currentImageRecord || State.currentUrl);
        }
      } catch (error) {
        errorViewer('Failed to load more modal images', error);
        Utils.toast('Failed to load more covers', 'error');
      } finally {
        this.setModalNextLoading(false);
        this.syncImageNavigation(State.currentImageRecord || State.currentUrl);
      }

      return false;
    }

    async navigateGlobal(direction) {
      if (!State.currentUrl) return;
      if (!this.globalSequence.length) this.globalSequence = this.getNavigableUrls();

      let currentIndex = this.globalSequence.indexOf(State.currentUrl);
      if (currentIndex < 0) currentIndex = this.globalIndex;
      if (currentIndex < 0) return;

      const nextIndex = currentIndex + direction;
      logViewer('Global navigation requested', {
        direction,
        currentIndex,
        nextIndex,
        sequenceSize: this.globalSequence.length,
        currentUrl: State.currentUrl,
      });
      if (nextIndex < 0 || nextIndex >= this.globalSequence.length) {
        if (direction > 0 && Utils.isLoggedIn() && State.hasMore) {
          await this.loadMoreAndAdvance(currentIndex);
        } else if (direction < 0) {
          Utils.toast('No previous cover art', 'info');
          this.syncGlobalNavigation(State.currentImageRecord || State.currentUrl);
        } else {
          Utils.toast('No next cover art', 'info');
          this.syncGlobalNavigation(State.currentImageRecord || State.currentUrl);
        }
        return;
      }

      this.openImage(this.globalSequence[nextIndex]);
    }

    async navigateLineage(direction) {
      const gallery = getService('gallery');
      const currentRecord = gallery?.resolveRecord?.(State.currentImageRecord || State.currentUrl);
      if (!currentRecord?.fileName || !this.lineageSequence.length) {
        warnViewer('Local lineage navigation requested without a server lineage sequence', {
          currentUrl: State.currentUrl,
          fileName: currentRecord?.fileName || null,
          sequenceSize: this.lineageSequence.length,
        });
        this.hideLineageNavigation();
        return;
      }

      let currentIndex = this.lineageSequence.findIndex((record) => {
        if (currentRecord.id != null && record.id != null) return record.id === currentRecord.id;
        if (currentRecord.fileName && record.fileName) return record.fileName === currentRecord.fileName;
        return record.url === currentRecord.url;
      });

      if (currentIndex < 0) currentIndex = this.lineageIndex;
      if (currentIndex < 0) return;

      const targetIndex = currentIndex + direction;
      logViewer('Local lineage navigation requested', {
        direction,
        coverGroupId: currentRecord.coverGroupId ?? null,
        fileName: currentRecord.fileName,
        currentIndex,
        targetIndex,
        sequenceSize: this.lineageSequence.length,
        lineageDepth: currentRecord.lineageDepth,
      });
      if (targetIndex >= 0 && targetIndex < this.lineageSequence.length) {
        this.openImage(this.lineageSequence[targetIndex]);
        return;
      }

      warnViewer('Local lineage navigation hit the end of the group', {
        direction,
        coverGroupId: currentRecord.coverGroupId ?? null,
        fileName: currentRecord.fileName,
        currentIndex,
        sequenceSize: this.lineageSequence.length,
      });
      Utils.toast(direction < 0 ? 'No previous version' : 'No next version', 'info');
      this.syncLineageNavigation(State.currentImageRecord || State.currentUrl);
    }

    openImage(input) {
      const gallery = getService('gallery');
      const record = gallery?.resolveRecord?.(input);
      const url = typeof input === 'string' ? input : record?.url;
      if (!url) return;

      State.currentUrl = url;
      State.currentImageRecord = record || null;
      logViewer('Opening image modal record', {
        url,
        recordId: record?.id ?? null,
        fileName: record?.fileName || null,
        coverGroupId: record?.coverGroupId ?? null,
        parentCoverId: record?.parentCoverId ?? null,
        lineageDepth: record?.lineageDepth ?? null,
      });
      const modalImage = Utils.$('modalImage');
      if (modalImage) modalImage.src = url;
      this.syncImageNavigation(record || url);
      if (Utils.isLoggedIn()) this.loadServerLineage(record || url);
      const app = getService('app');
      if (app && typeof app.updateEditReferencePreview === 'function') app.updateEditReferencePreview(url);
      const modalTitle = Utils.$('#modal-title');
      if (modalTitle) modalTitle.textContent = this.getModalTitle(record, url);

      const loggedInView = Utils.$('imageModalLoggedIn');
      const guestView = Utils.$('imageModalGuest');

      if (Utils.isLoggedIn()) {
        loggedInView?.classList.remove('hidden');
        guestView?.classList.add('hidden');
        const freeResultScreen = Utils.$('#free-result-screen');
        const isFreeWatermarked = false;
        freeResultScreen?.classList.toggle('hidden', !isFreeWatermarked);
        this.resetMakeOfficialForm();
        if (app && typeof app.closeEditPanel === 'function') app.closeEditPanel();
        const editText = Utils.$('editText');
        if (editText) editText.value = '';
        getService('editUploader')?.reset();
      } else {
        loggedInView?.classList.add('hidden');
        guestView?.classList.remove('hidden');
        Utils.$('#free-result-screen')?.classList.add('hidden');
        this.resetMakeOfficialForm();
        const info = Config.guestImageMap[url] || Config.guestImages[0];
        if (info) {
          Utils.$('#guest-info-artist').textContent = info['Artist Name'];
          Utils.$('#guest-info-project').textContent = info['Song/Album Name'];
          Utils.$('#guest-info-genre').textContent = info.Genre;
          Utils.$('#guest-info-concept').textContent = info['Visual Concept'];
        }
      }

      const modal = Utils.$('imageModal');
      modal?.classList.remove('hidden');
      Utils.syncBodyLock();
    }

    closeImageModal() {
      const app = getService('app');
      if (app && typeof app.closeEditPanel === 'function') app.closeEditPanel();
      if (app && typeof app.closeEditReferencePreview === 'function') app.closeEditReferencePreview();
      Utils.$('imageModal')?.classList.add('hidden');
      this.resetMakeOfficialForm();
      if (this.modalImageNav) {
        this.modalImageNav.classList.add('hidden');
        this.modalImageNav.classList.remove('flex');
      }
      this.hideLineageNavigation();
      this.lineageRequestToken += 1;
      if (this.modalNextLoading) this.modalNextLoading.classList.add('hidden');
      this.globalSequence = [];
      this.globalIndex = -1;
      this.globalAnchorUrl = null;
      State.currentImageRecord = null;
      Utils.syncBodyLock();
    }

    async submitEdit() {
      if (State.credits < Config.creditsEdit) {
        Events.emit('modal:buy-open-requested');
        return;
      }

      const editInput = Utils.$('editText');
      const txt = String(editInput?.value || '').trim();
      if (!txt) {
        Utils.toast('Description required', 'warn');
        return;
      }

      Utils.$('editLoading')?.classList.remove('hidden');

      try {
        const activeRecord = State.currentImageRecord || {};
        const isCreativeLabAsset = String(activeRecord.assetFamily || '').trim() === 'creative_lab';
        TaskManager.createTask({
          type: isCreativeLabAsset ? 'creative-edit' : 'edit',
          title: 'Edit Request',
          artist: txt.substring(0, 30) + (txt.length > 30 ? '...' : ''),
          assetType: activeRecord.assetType || 'creative_lab',
          assetTypeLabel: activeRecord.assetType || 'Creative Lab',
          sizePreset: activeRecord.sizePreset || 'square_1_1',
          editRequest: txt,
          imageUrl: State.currentUrl,
          sourceFileName: activeRecord.fileName || activeRecord.uniqueFileName || '',
          templateInput: State.editTemplate ? State.editTemplate.imageUrl : null,
          photoInputs: getService('editUploader')?.uploadedUrls?.length > 0 ? getService('editUploader').uploadedUrls : null,
          assetInputs: getService('editUploader')?.uploadedUrls?.length > 0 ? getService('editUploader').uploadedUrls : null,
          toolSlug: activeRecord.toolSlug || Config.toolSlug,
          nicheSlug: activeRecord.nicheSlug || Config.nicheSlug,
          assetFamily: activeRecord.assetFamily || Config.assetFamily,
          assetType: activeRecord.assetType || Config.assetType,
          styleSlug: activeRecord.styleSlug || '',
          styleName: activeRecord.styleName || '',
          canvasReady: activeRecord.canvasReady === true,
        });

        Utils.toast('Edit queued - working on it now.');

        if (editInput) editInput.value = '';
        State.editTemplate = null;
        getService('editUploader')?.reset();
        Utils.$('editSelectedTemplate')?.classList.add('hidden');
        const app = getService('app');
        if (app && typeof app.closeEditPanel === 'function') app.closeEditPanel();
      } catch (error) {
        errorViewer('Failed to submit edit', error);
        Utils.toast('Failed to submit edit', 'error');
      } finally {
        Utils.$('editLoading')?.classList.add('hidden');
      }
    }
  }

  runtime.ImageViewerController = ImageViewerController;
})();
